import type { ControllerPlacementCandidateTable } from "building-types/electrical/candidate";
import type { ElectricalSlots } from "building-types/electrical/electricalSlots";
import type { GeneratedElectricalGroup } from "building-types/electrical/generatedElectricalTypes";
import type { ElectricalRuleHandle } from "building-types/handles/docEntityHandleTypes";
import type { Level } from "building-types/levels/levelTypes";
import type { Prop, PropAttributeTable } from "building-types/props/propTypes";
import type { RoomOutlines } from "building-types/rooms/roomOutlineTypes";
import type { Room } from "building-types/rooms/roomTypes";
import type { StaticRoom } from "building-types/rooms/staticRoomTypes";
import type { ElectricalItemCatalogSettings } from "building-types/settings/electricalItemCatalogSettings";
import type { ElectricalSettings } from "building-types/settings/electricalSettings";
import type { WallSystem } from "building-types/walls/wallSytemTypes";
import type { RWTable, Table } from "common/data/table";
import { DocEntityHandleSet } from "doc/entity/DocEntityHandleSet";
import { electricalRuleProvenanceSettingsId } from "doc/generate/deriveElectricalSettingsProvenance";
import type { SettingsFieldProvenance } from "doc/generate/deriveSettingsProvenance";
import type { MergedPropLibrary } from "doc/propTools";
import type { RoomSetsByLevelId } from "doc/rooms/roomSetTypes";
import { shouldApplyElectricalLayoutRulesToRoom } from "doc/utils/electricalRuleUtils";
import type { ControllerSelectionFunc } from "../controllerSelection/controllerSelectionShared";
import { convertControllerSelectionFuncToRoomSetIndexFunc } from "../controllerSelection/controllerSelectionShared";
import type { RoomFlagTable, RoomSetTable, RuleToRoomFlagTable } from "../electricalLayoutTypes";
import type { RoomSetInfoTable } from "../electricalRoomSetInfo";
import { makeElectricalLayout } from "../roomLayouts/electricalLayouts";
import { embedHandleInElectricalGroups, type ElectricalGroupsByRoomOrDocResults } from "./electricalGroupsShared";

export interface ElectricalGroupsFromRoomElectricalRulesResult {
  readonly electrical: GeneratedElectricalGroup[];
  /**
   * Every rule whose filters match a room, keyed by doc room id. Recorded even when the room
   * suppresses electrical from settings, so the Room inspector can show which rules a room is
   * subject to (or would be, once suppression is turned off).
   *
   * Accumulated as a `DocEntityHandleSet` for hash-lookup dedup, but handed back as plain arrays:
   * this lands on the `Building`, which is plain data throughout, and consumers only iterate it.
   */
  readonly electricalRulesByRoom: Table<readonly ElectricalRuleHandle[]>;
}

export async function electricalGroupsFromRoomElectricalRules(
  levels: Table<Level>,
  roomsTable: Table<Room>,
  candidateTable: ControllerPlacementCandidateTable,
  roomSetsByLevelId: RoomSetsByLevelId,
  wallSystems: Table<WallSystem>,
  electricalSettings: ElectricalSettings,
  electricalItemCatalogSettings: ElectricalItemCatalogSettings,
  electricalByRoomBlockDocResults: ElectricalGroupsByRoomOrDocResults,
  props: Table<Prop>,
  selectionFunc: ControllerSelectionFunc,
  roomSetInfoTable: RoomSetInfoTable,
  propCatalog: PropAttributeTable,
  mergedPropLibrary: MergedPropLibrary,
  slots: ElectricalSlots,
  mirror: boolean,
  roomOutlines: Table<RoomOutlines>,
  docRooms: Table<StaticRoom>,
  electricalSettingsProvenance: SettingsFieldProvenance,
): Promise<ElectricalGroupsFromRoomElectricalRulesResult> {
  const electrical: GeneratedElectricalGroup[] = [];
  const electricalRuleSetsByRoom: RWTable<DocEntityHandleSet<ElectricalRuleHandle>> = {};

  if (!electricalByRoomBlockDocResults) {
    return { electrical, electricalRulesByRoom: {} };
  }

  const { clearRoomTypeElectrical, electricalGroups: roomBlockHomeElectricalGroups } = electricalByRoomBlockDocResults;
  const { rules } = electricalSettings;

  const electricalRuleHandleForRule = (ruleId: string): ElectricalRuleHandle | undefined => {
    const settingsId = electricalRuleProvenanceSettingsId(electricalSettingsProvenance, ruleId);
    if (settingsId === undefined) {
      return undefined;
    }
    return { type: "ELECTRICAL_RULE", settingsHandle: { type: "SETTINGS", id: settingsId }, id: ruleId };
  };

  for (const levelId in levels) {
    const levelRoomSets = roomSetsByLevelId[levelId] ?? [];
    const wallSystem = wallSystems[levelId];
    const wallIndex = wallSystem.wallIndex;

    const levelRoomBlockHomeGroups = roomBlockHomeElectricalGroups.filter((x) => x.levelId === levelId);
    electrical.push(...levelRoomBlockHomeGroups);

    for (const { roomSet, id } of levelRoomSets) {
      const { spaceTypesTable } = roomSetInfoTable[id];

      const ruleCanBeAppliedToRoomTable: RuleToRoomFlagTable = {};
      const roomSetTable: RoomSetTable = {};

      //preflight - store rules and the rooms they apply to
      for (const { id: roomId } of roomSet) {
        const room = roomsTable[roomId];
        roomSetTable[roomId] = room;

        if (clearRoomTypeElectrical[roomId]) {
          continue;
        }

        // Instance rooms inherit this suppression from their source doc room.
        const sourceRoomId = room?.sourceRoomId ?? roomId;
        const suppressed = !!docRooms[sourceRoomId]?.suppressElectricalFromSettings;

        const spaceType = spaceTypesTable[roomId];

        for (const ruleId in rules) {
          const { id, filters } = rules[ruleId];
          const apply = shouldApplyElectricalLayoutRulesToRoom(room, spaceType, filters);
          if (!apply) {
            continue;
          }

          recordMatchingRuleForRoom(electricalRuleSetsByRoom, sourceRoomId, electricalRuleHandleForRule(id));

          // Matches are still recorded above so the room inspector can list what suppression hides.
          if (suppressed) {
            continue;
          }

          const roomFlagTable = ruleCanBeAppliedToRoomTable[id] || {};
          roomFlagTable[roomId] = true;
          ruleCanBeAppliedToRoomTable[id] = roomFlagTable;
        }
      }

      for (const ruleId in ruleCanBeAppliedToRoomTable) {
        const roomsInRoomSetRuleCanBeAppliedTo = ruleCanBeAppliedToRoomTable[ruleId];
        const ruleAppliedToRoomTable: RoomFlagTable = {};
        const { electricalLayout } = rules[ruleId];
        const electricalRuleHandle = electricalRuleHandleForRule(ruleId);

        for (const roomId in roomsInRoomSetRuleCanBeAppliedTo) {
          if (ruleAppliedToRoomTable[roomId]) {
            continue;
          }
          const room = roomsTable[roomId];
          const roomProps = Object.values(props).filter((x) => x.room?.id === roomId);
          const roomOutlineTable = roomOutlines[roomId];

          const electricalGroups = await makeElectricalLayout(electricalLayout, {
            roomSetTable,
            room,
            wallIndex,
            candidateTable,
            controllerSelectionFunc: convertControllerSelectionFuncToRoomSetIndexFunc(selectionFunc, roomId),
            roomProps,
            propCatalog,
            mergedPropLibrary,
            electricalItemCatalogSettings,
            elecSlots: slots,
            mirror,
            roomOutlines: roomOutlineTable,
            ruleAppliedToRoomTable,
            roomsInRoomSetRuleCanBeAppliedTo,
          });

          if (!electricalGroups) {
            continue;
          }

          if (!!electricalRuleHandle) {
            embedHandleInElectricalGroups(electricalGroups, electricalRuleHandle, electricalRuleHandle);
          }

          electrical.push(...electricalGroups);
        }
      }
    }
  }

  return { electrical, electricalRulesByRoom: electricalRuleHandleArraysByRoom(electricalRuleSetsByRoom) };
}

function recordMatchingRuleForRoom(
  electricalRuleSetsByRoom: RWTable<DocEntityHandleSet<ElectricalRuleHandle>>,
  docRoomId: string,
  handle: ElectricalRuleHandle | undefined,
): void {
  if (!handle) {
    return;
  }
  const handles = electricalRuleSetsByRoom[docRoomId] ?? new DocEntityHandleSet<ElectricalRuleHandle>();
  handles.add(handle);
  electricalRuleSetsByRoom[docRoomId] = handles;
}

function electricalRuleHandleArraysByRoom(
  electricalRuleSetsByRoom: Table<DocEntityHandleSet<ElectricalRuleHandle>>,
): Table<readonly ElectricalRuleHandle[]> {
  const electricalRulesByRoom: RWTable<readonly ElectricalRuleHandle[]> = {};
  for (const docRoomId in electricalRuleSetsByRoom) {
    electricalRulesByRoom[docRoomId] = electricalRuleSetsByRoom[docRoomId].toArray();
  }
  return electricalRulesByRoom;
}
